import { FC } from "react";
import {
  ConfigurationListItem,
  ConfigurationListItemKind,
  ConfigurationPermissionStatus,
} from "../configuration.types";
import {
  RowContainer,
  RowLeft,
  RowTitle,
  RowDescription,
  RowRight,
  RowRightColumn,
} from "../configuration.styles";
import {
  MDSButton,
  MDSButtonVariant,
  MDSIcon,
  MDSIconColor,
  MDSIconKind,
  MDSText,
  MDSTextColor,
  MDSTextVariant,
  MDSTextWeight,
} from "@/mds";
import styled from "@emotion/styled";

export const ConfigurationListPermissionRow: FC<
  Extract<ConfigurationListItem, { kind: ConfigurationListItemKind.Permission }>
> = ({ title, description, status, onEnable, onOpenSystemPreferences }) => {
  const isGranted = status === ConfigurationPermissionStatus.Granted;
  const isLoading = status === ConfigurationPermissionStatus.Loading;
  const isDenied = status === ConfigurationPermissionStatus.Denied;
  const isTimeout = status === ConfigurationPermissionStatus.Timeout;
  const needsPreferences = isDenied || isTimeout;
  const handleClick =
    needsPreferences && onOpenSystemPreferences ? onOpenSystemPreferences : onEnable;

  return (
    <RowContainer>
      <RowLeft>
        <RowTitle variant={MDSTextVariant.ParagraphSm} weight={MDSTextWeight.Medium}>
          {title}
        </RowTitle>
        {description ? (
          <RowDescription variant={MDSTextVariant.TextS}>{description}</RowDescription>
        ) : null}
      </RowLeft>
      <RowRight>
        {isGranted ? (
          <EnabledStatusBadge>
            <MDSIcon kind={MDSIconKind.CheckCircleSolid} color={MDSIconColor.Positive} />
            <MDSText variant={MDSTextVariant.TextSm} color={MDSTextColor.Positive}>
              Enabled
            </MDSText>
          </EnabledStatusBadge>
        ) : (
          <RowRightColumn>
            {isDenied ? (
              <DeniedStatusBadge>
                <MDSIcon kind={MDSIconKind.CircleExclamation} color={MDSIconColor.Danger} />
                <MDSText variant={MDSTextVariant.TextSm} color={MDSTextColor.Danger}>
                  Denied
                </MDSText>
              </DeniedStatusBadge>
            ) : null}
            <ActionButton
              variant={MDSButtonVariant.Default}
              onClick={handleClick}
              isLoading={isLoading}
            >
              {needsPreferences ? "Open System Preferences" : "Enable"}
            </ActionButton>
          </RowRightColumn>
        )}
      </RowRight>
    </RowContainer>
  );
};

const ActionButton = styled(MDSButton)`
  min-width: 150px;
`;

const StatusBadge = styled.span`
  width: 100%;
  display: inline-flex;
  align-items: center;
  justify-content: flex-end;
  gap: ${({ theme }) => theme.spacing["1"]};
  white-space: nowrap;
  user-select: none;
`;

const EnabledStatusBadge = styled(StatusBadge)`
  padding-right: ${({ theme }) => theme.spacing["1"]};
`;

const DeniedStatusBadge = styled(StatusBadge)`
  justify-content: flex-start;
`;
